import { Link } from "react-router-dom";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { origin } from "../lib/origin";
import { MIN_REVIEWS_TO_SCORE } from "../../shared/methodology";

// Who publishes this, how it is paid for, and where the data comes from.
//
// Written only from things that are true of the running system. It names no
// individual and invents no team, credentials or history: a page whose job is
// to make the site checkable cannot open with something unverifiable.

export function About() {
  useMeta(metaFor({ route: "about", base: origin() }));

  return (
    <div className="narrow-lg">
      <h1>About CryptoChit</h1>
      <p className="muted">
        A directory of crypto exchanges, wallets, protocols and tokens, where a review can carry
        cryptographic proof that the reviewer actually used the product.
      </p>

      <section className="section">
        <h2>Why this exists</h2>
        <p>
          Crypto review sites have a structural problem: anyone can write anything, the products
          hold real money, and the sites that rank them are usually paid by the companies being
          ranked. Nothing in a five-star review tells you whether the person ever opened an
          account.
        </p>
        <p>
          Crypto is unusual in that this is fixable. If you held a token, you can sign a message
          from the wallet that held it. If you traded on an exchange, you can produce a read-only
          key that proves account activity. So a review here can be checked rather than
          believed, and every review says which kind it is.
        </p>
      </section>

      <section className="section">
        <h2>How this site makes money</h2>
        <p>Stated plainly, because it is the first thing worth being suspicious about.</p>
        <ul className="policy-list">
          <li>
            <strong>Listing services.</strong> A company can pay for a decision on a submitted
            listing within a promised window, or for staff to build the profile. Both buy time or
            labour. A paid submission can still be rejected, and a rejection is refunded.
          </li>
          <li>
            <strong>Featured placement.</strong> Sold, and labelled as sold wherever it appears.
            It sits outside the ranked list rather than reordering it.
          </li>
          <li>
            <strong>Claiming a profile and replying to reviews is free.</strong> Charging a
            company to answer its critics would be a protection racket.
          </li>
        </ul>
        <p>
          What is not for sale: a rating, a Trust Score, a ranking position, or the removal of a
          review. This is not a promise to take on trust — the code that ranks and scores
          listings is never handed billing information, so it has no way to know who pays, and
          automated tests fail the build if anyone connects the two.
        </p>
      </section>

      <section className="section">
        <h2>Where the data comes from</h2>
        <ul className="policy-list">
          <li>
            <strong>Listings.</strong> Company details from public sources; tokens from
            market-capitalisation rankings, with every contract address confirmed against a live
            chain scanner before it was published. A contract address appears on at most one
            listing, because two listings claiming one contract means at least one is wrong about
            what a reader would be buying.
          </li>
          <li>
            <strong>Reviews.</strong> Written by people, never by us. The imported catalogue
            contains no reviews at all, and none are generated.
          </li>
          <li>
            <strong>Ratings.</strong> Computed from published reviews. A listing needs at least{" "}
            {MIN_REVIEWS_TO_SCORE} before it shows a score, and shows nothing rather than a number
            built on a single opinion.
          </li>
          <li>
            <strong>Market and security data.</strong> Public sources and automated contract
            scans, refreshed on a schedule and stamped with when they were read.
          </li>
        </ul>
      </section>

      <section className="section">
        <h2>What this site does not do</h2>
        <p>
          It does not give investment advice, predict prices, or recommend buying or selling
          anything. It does not write reviews, and it does not publish a rating for a listing
          nobody has reviewed — an absent score means nobody has reviewed it, not that it scored
          badly.
        </p>
      </section>

      <section className="section">
        <h2>Corrections and disputes</h2>
        <p>
          If a listing is wrong, claim the profile and correct it. If a review breaks the rules,
          dispute it from the review itself and a moderator will look at it. A review removed for
          breaking the rules leaves a visible note saying so, rather than disappearing quietly.
        </p>
        <div className="row gap-8 wrap">
          <Link to="/dashboard/claim" className="btn btn-primary btn-sm">Claim a profile</Link>
          <Link to="/methodology" className="btn btn-ghost btn-sm">Read the methodology</Link>
        </div>
      </section>

      <section className="section">
        <h2>Who publishes this</h2>
        <p>
          CryptoChit is an independent directory. It is not owned by, funded by, or operated on
          behalf of any exchange, wallet, protocol or token issuer listed on it.
        </p>
      </section>

      <div className="nfa section">
        <strong>Not financial advice.</strong> CryptoChit publishes user opinions and verifiable
        on-chain facts. Always do your own research.
      </div>
    </div>
  );
}
